import {
	faArrowDownLong as fasArrowDownLong,
	faChevronRight as fasChevronRight,
	faCircleQuestion as fasCircleQuestion,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import type { MeasureComponentProps } from "@component/MeasureComponent";
import type { ProductType } from "@component/assets/AbstractLeadForm/types";
import type { AbstractDataPageState } from "@component/assets/AbstractListingCalculator/AbstractDataPage";
import { AbstractDataPage } from "@component/assets/AbstractListingCalculator/AbstractDataPage";
import { Config } from "@config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import { http, Button, HtmlBlock, InfoPopup } from "bm-ui-kit";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import React from "react";
import type { ReactNode } from "react";
import { Col, Container, Row, Table } from "react-bootstrap";
import { searchParamsToRequest } from "../Calculator/domain";
import type { MutualFundResponseItem, MutualFundSearchParams } from "../Calculator/types";
import "./style.scss";

interface Props extends MeasureComponentProps {
	isin: string;
}

interface State extends AbstractDataPageState<MutualFundResponseItem> {
	showDetailsModal: boolean;
	detailsModalContent: ReactNode;
	detailsModalTitle: ReactNode;
	exchangeRateChartData: any[];
	showFullDescription: boolean;
}

export class MutualFundDataPage extends AbstractDataPage<MutualFundSearchParams, MutualFundResponseItem, Props, State> {
	protected readonly productTypes: string = "MUTUAL_FUND";
	protected readonly componentType: EventComponentType = "product-page";

	protected getInitialState(): State {
		const parentState = super.getInitialState();

		return {
			...parentState,
			showDetailsModal: false,
			detailsModalContent: "",
			detailsModalTitle: "",
			exchangeRateChartData: [],
			showFullDescription: false,
		} as unknown as State;
	}

	protected loadProductData(searchParams: MutualFundSearchParams, _id: string): Promise<MutualFundResponseItem> {
		const request = searchParamsToRequest(searchParams);
		const { isin } = this.props;
		const url = Config.getApiUrl(
			`/api/public/mutual-fund/api-for-bankmonitor-hu/v4/mutual-funds/details/${encodeURIComponent(isin)}`,
		);

		return http.get(url, request);
	}

	protected redirectToCalculator(): void {
		window.location.href = `/megtakaritasok/befektetesi-alap/`;
	}

	protected onProductDataLoaded(): void {
		this.loadExchangeRateData();
	}

	protected renderHeader(): ReactNode {
		const { responseItem } = this.state;
		document.title = `${responseItem.name} | Bankmonitor.hu`;

		return (
			<Container>
				<div className="d-flex align-items-end my-4 pb-2 border-bottom">
					<div className="d-inline-block text-truncate">
						<h1 className="my-0 text-truncate">{responseItem.name}</h1>
						Árfolyam ({responseItem.calculation.date[0]}. {responseItem.calculation.date[1]}.{" "}
						{responseItem.calculation.date[2]}.): <strong>{responseItem.calculation.exchangeRate}</strong>
					</div>
					{this.renderOneYear()}
				</div>
			</Container>
		);
	}

	private renderOneYear(): ReactNode {
		const { responseItem } = this.state;

		if (responseItem.calculation.oneYear) {
			return (
				<div className="ml-auto pl-3 text-nowrap">
					<strong
						className={`h3 border-bottom ${responseItem.calculation.oneYear < 0 ? "text-danger" : ""}${
							responseItem.calculation.oneYear > 0 ? "text-success" : ""
						}`}
					>
						{responseItem.calculation.oneYear > 0 && (
							<FontAwesomeIcon icon={fasArrowDownLong} className="mr-2" rotation={180} />
						)}
						{responseItem.calculation.oneYear < 0 && (
							<FontAwesomeIcon icon={fasArrowDownLong} className="mr-2" />
						)}
						{formatText(responseItem.calculation.oneYear, "%")}
					</strong>{" "}
					egy év alatt
				</div>
			);
		}
	}

	private loadExchangeRateData(): void {
		const { responseItem } = this.state;

		this.promisePool
			.add(
				http.get(
					Config.getApiUrl(
						`/api/public/mutual-fund/api-for-bankmonitor-hu/v4/mutual-funds/exchange-rates/${encodeURIComponent(responseItem.currency.code)}/${encodeURIComponent(responseItem.isin)}`,
					),
				),
			)
			.then((data: any) => {
				this.setState({
					exchangeRateChartData: data,
				});
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			});
	}

	public renderContent(): ReactNode {
		return (
			<Container>
				{this.renderMarketingBlock()}
				{this.renderBasicData()}
				{this.renderYield()}
				{this.renderExchangeRatesGraph()}
				<Row className="mb-4">
					<Col md={6}>
						{this.renderRiskProperties()}
						{this.renderExpenses()}
						{this.renderAnnualYieldsGraph()}
					</Col>
					<Col md={6}>
						{this.renderDescription()}
						{this.renderManagerAndBenchmarks()}
					</Col>
				</Row>
			</Container>
		);
	}

	private renderBasicData(): ReactNode {
		const { responseItem } = this.state;

		return (
			<>
				<div className="h3 border-bottom">Befektetési alap tulajdonságai</div>
				<Table striped bordered>
					<tbody>
						<tr>
							<th scope="row">Devizanem:</th>
							<td>{responseItem.currency.code}</td>
						</tr>
						<tr>
							<InfoPopup
								as="th"
								tooltip="Minél magasabb kockázatú befektetést választasz, annál nagyobb valószínűséggel érsz el átlagnál magasabb hozamot, de ezzel párhuzamosan a veszteség valószínűsége is ugyanígy megnő."
							>
								Kockázati szint: <FontAwesomeIcon icon={fasCircleQuestion} className="info" />
							</InfoPopup>
							<td>{responseItem.category.timeFrame.riskLevelLabel}</td>
						</tr>
						<tr>
							<InfoPopup
								as="th"
								tooltip="A befektetési alapok, attól függően, hogy mibe fektetnek, milyen kockázatot vállalnak, illetve milyen befektetési célt tűztek ki maguk elé, különböző kategóriákba tartoznak."
							>
								Kategória: <FontAwesomeIcon icon={fasCircleQuestion} className="info" />
							</InfoPopup>
							<td>{responseItem.category.name}</td>
						</tr>
						<tr>
							<InfoPopup
								as="th"
								tooltip="Az a minimális időtartam, amire az adott befektetés kockázatát figyelembe véve érdemes az alapot megvásárolni. Legalább ennyi eddig kell a pénzünk ebben az a lapban lévő részét nélkülözni."
							>
								Ajánlott időtáv: <FontAwesomeIcon icon={fasCircleQuestion} className="info" />
							</InfoPopup>
							<td>{responseItem.category.timeFrame.timeFrameLabel}</td>
						</tr>
						<tr>
							<th scope="row">Alapkezelő:</th>
							<td>{responseItem.managementBank.name}</td>
						</tr>
						{responseItem.netAssetValue && (
							<tr>
								<th scope="row">Alap vagyona:</th>
								<td>{formatText(responseItem.netAssetValue, "ft")}</td>
							</tr>
						)}
						<tr>
							<th scope="row">Földrajzi kiterjedtség:</th>
							<td>{responseItem.geographicalExposure.name}</td>
						</tr>
						<tr>
							<th scope="row">Indulás ideje:</th>
							<td>
								{responseItem.startDate[0]}. {responseItem.startDate[1]}. {responseItem.startDate[2]}.
							</td>
						</tr>
						<tr>
							<InfoPopup
								as="th"
								tooltip="A befektetési alap jegyeinek egyedi azonosítószáma. Az egyértelmű beazonosítást segíti."
							>
								ISIN: <FontAwesomeIcon icon={fasCircleQuestion} className="info" />
							</InfoPopup>
							<td>{responseItem.isin}</td>
						</tr>
					</tbody>
				</Table>
			</>
		);
	}

	private renderYield(): ReactNode {
		const { responseItem } = this.state;

		return (
			<>
				<div className="h3 border-bottom">Befektetési alap tulajdonságai</div>
				<Table striped bordered>
					<thead>
						<tr>
							<th scope="col">3 hó</th>
							<th scope="col">6 hó</th>
							<th scope="col">1 év</th>
							<th scope="col">3 év</th>
							<th scope="col">5 év</th>
							<th scope="col">10 év</th>
							<th scope="col">YTD</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>
								{responseItem.calculation.threeMonth
									? formatText(responseItem.calculation.threeMonth, "%")
									: "-"}
							</td>
							<td>
								{responseItem.calculation.sixMonth
									? formatText(responseItem.calculation.sixMonth, "%")
									: "-"}
							</td>
							<td>
								{responseItem.calculation.oneYear
									? formatText(responseItem.calculation.oneYear, "%")
									: "-"}
							</td>
							<td>
								{responseItem.calculation.threeYear
									? formatText(responseItem.calculation.threeYear, "%")
									: "-"}
							</td>
							<td>
								{responseItem.calculation.fiveYear
									? formatText(responseItem.calculation.fiveYear, "%")
									: "-"}
							</td>
							<td>
								{responseItem.calculation.tenYear
									? formatText(responseItem.calculation.tenYear, "%")
									: "-"}
							</td>
							<td>
								{responseItem.calculation.ytd ? formatText(responseItem.calculation.ytd, "%") : "-"}
							</td>
						</tr>
					</tbody>
				</Table>
			</>
		);
	}

	private renderExchangeRatesGraph(): ReactNode {
		const { responseItem, exchangeRateChartData } = this.state;

		// Grafikon
		if (!exchangeRateChartData.length) {
			return <div>Nincs adat.</div>;
		}

		const options: Highcharts.Options = {
			credits: {
				enabled: false,
			},
			title: {
				text: null,
			},
			series: [
				{
					name: responseItem.currency.name,
					type: "area",
					color: "#DEE2E6",
					data: exchangeRateChartData,
				},
			],
			xAxis: {
				type: "datetime",
				dateTimeLabelFormats: {
					day: {
						main: "%Y.<br/>%m. %d.",
					},
					week: {
						main: "%Y.<br/>%m. %d.",
					},
					month: {
						main: "%Y. %m.",
					},
					year: {
						main: "%Y.",
					},
				},
			},
			tooltip: {
				headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
				pointFormat:
					'<tr><td style="color:{series.color};padding:0">{series.name}: </td><td style="padding:0"><b>{point.y:.2f} </b></td></tr>',
				footerFormat: "</table>",
				shared: true,
				useHTML: true,
			},
			navigator: {
				enabled: true,

				xAxis: {
					type: "datetime",
					dateTimeLabelFormats: {
						day: {
							main: "%Y.<br/>%m. %d.",
						},
						week: {
							main: "%Y.<br/>%m. %d.",
						},
						month: {
							main: "%Y. %m.",
						},
						year: {
							main: "%Y.",
						},
					},
				},
			},
			rangeSelector: {
				inputDateFormat: "%Y-%m-%d",
				buttons: [
					{
						type: "ytd",
						text: "YTD",
					},
					{
						type: "month",
						count: 12,
						text: "1 év",
					},
					{
						type: "month",
						count: 36,
						text: "3 év",
					},
					{
						type: "month",
						count: 60,
						text: "5 év",
					},
					{
						type: "all",
						text: "Teljes",
					},
				],
				buttonTheme: {
					width: 40,
				},
			},
		};

		return (
			<>
				<span className="h3 border-bottom">Árfolyam grafikon</span>
				<HighchartsReact highcharts={Highcharts} options={options} constructorType="stockChart" />
			</>
		);
	}

	private renderRiskProperties(): ReactNode {
		const {
			responseItem: { calculation },
		} = this.state;

		return (
			<>
				<div className="h3 border-bottom">Alap kockázati jellemzői</div>
				<Table striped bordered>
					<tbody>
						<tr>
							<InfoPopup
								as="th"
								tooltip="A kockázat mérésére alkalmazott mutató. Minél magasabb, annál kockázatosabb az alap. Megmutatja a befektetésünk egy év alatti hozamingadozását."
							>
								Szórás: <FontAwesomeIcon icon={fasCircleQuestion} className="info" />
							</InfoPopup>
							<td>{calculation.annualDeviation ? formatText(calculation.annualDeviation, "%") : "-"}</td>
						</tr>
						<tr>
							<InfoPopup
								as="th"
								tooltip="Egy mutatóba sűríti a kockázat és hozam összefüggését. Azt mutatja, hogy egységnyi extra kockázatért cserébe milyen extra hozamot nyújt a befektetésünk."
							>
								Sharpe mutató: <FontAwesomeIcon icon={fasCircleQuestion} className="info" />
							</InfoPopup>
							<td>{calculation.sharpeRatio ? calculation.sharpeRatio.toFixed(2) : "-"}</td>
						</tr>
						<tr>
							<InfoPopup
								as="th"
								tooltip="Megmutatja az alap történetében addig bekövetkezett legnagyobb árfolyamveszteségét. (Legrosszabb esetben ennyit veszíthetett egy befektető az alappal.)"
							>
								Max. árfolyam veszteség: <FontAwesomeIcon icon={fasCircleQuestion} className="info" />
							</InfoPopup>
							<td>
								{calculation.maxDrawDownValue ? formatText(calculation.maxDrawDownValue, "%") : "-"}
							</td>
						</tr>
						<tr>
							<th scope="row">Ennyi idő alatt:</th>
							<td>{calculation.maxDrawDownLength ? `${calculation.maxDrawDownLength} nap` : "-"}</td>
						</tr>
						<tr>
							<InfoPopup
								as="th"
								tooltip="Megmutatja az alap történetében eddig bekövetkezett legnagyobb árfolyamnyereséget. (Legjobb esetben ennyit nyerhetett egy befektető az alappal.)"
							>
								Max. árfolyam nyereség: <FontAwesomeIcon icon={fasCircleQuestion} className="info" />
							</InfoPopup>
							<td>{calculation.maxDrawUpValue ? formatText(calculation.maxDrawUpValue, "%") : "-"}</td>
						</tr>
						<tr>
							<th scope="row">Ennyi idő alatt:</th>
							<td>{calculation.maxDrawUpLength ? `${calculation.maxDrawUpLength} nap` : "-"}</td>
						</tr>
					</tbody>
				</Table>
			</>
		);
	}

	private renderExpenses(): ReactNode {
		const { responseItem } = this.state;

		if (!responseItem.ongoingCharges && !responseItem.performanceFeeBenchmark) {
			return null;
		}

		return (
			<>
				<div className="h3 border-bottom">Költségek</div>
				<Table striped bordered>
					<tbody>
						<tr>
							<th scope="row">Folyó költségek / év:</th>
							<td>{responseItem.ongoingCharges ? formatText(responseItem.ongoingCharges, "%") : "-"}</td>
						</tr>
						<tr>
							<th scope="row">Sikerdíj:</th>
							<td>
								{responseItem.performanceFeeBenchmark
									? formatText(responseItem.performanceFeeBenchmark, "%")
									: "-"}
							</td>
						</tr>
					</tbody>
				</Table>
			</>
		);
	}

	private renderAnnualYieldsGraph(): ReactNode {
		const { responseItem } = this.state;

		const series = [];
		for (const index in responseItem.calculation.annualYields) {
			series.push([index, responseItem.calculation.annualYields[index]]);
		}

		if (series.length > 0) {
			const options: Highcharts.Options = {
				credits: {
					enabled: false,
				},
				chart: {
					type: "column",
				},
				title: {
					text: null,
				},
				series: [
					{
						type: "column",
						name: "Hozam (%)",
						color: "#DEE2E6",
						data: series,
					},
				],
				xAxis: {
					type: "linear",
					labels: {
						formatter(this) {
							return series[this.value][0];
						},
					},
				},
				yAxis: {
					title: {
						text: "Hozam (%)",
					},
					labels: {
						formatter(this) {
							return formatText(this.value, "%");
						},
					},
				},
				tooltip: {
					formatter(this) {
						return `<span>${this.points[0].key}:<br></span><b>${formatText(this.y, "%")}</b>`;
					},
					useHTML: true,
				},
				rangeSelector: {
					enabled: false,
				},
				scrollbar: {
					enabled: false,
				},
				navigator: {
					enabled: false,
				},
			};

			return (
				<>
					<span className="h3 border-bottom">Éves hozam</span>
					<HighchartsReact highcharts={Highcharts} options={options} constructorType="stockChart" />
				</>
			);
		}

		return null;
	}

	private renderDescription(): ReactNode {
		const { responseItem, showFullDescription } = this.state;

		if (responseItem.investmentPolicy) {
			const fulldesc = responseItem.investmentPolicy.length < 600 || showFullDescription;

			return (
				<>
					<span className="h3 border-bottom">Alap leírása</span>
					<HtmlBlock
						className={`${fulldesc ? "" : "truncated-description"}`}
						html={responseItem.investmentPolicy}
					/>
					{!fulldesc && (
						<div className="text-center mt-3">
							<Button
								icon={<FontAwesomeIcon icon={fasChevronRight} rotation={90} />}
								onClick={() => {
									this.setState({ showFullDescription: true });
								}}
							>
								Teljes szöveg
							</Button>
						</div>
					)}
				</>
			);
		}
	}

	private renderManagerAndBenchmarks(): ReactNode {
		const { responseItem } = this.state;

		if (!responseItem.benchmarks && !responseItem.mutualFundManagerPerson) {
			return null;
		}

		return (
			<>
				<span className="h3 border-bottom mt-3">Alapkezelő és referencia index</span>

				{responseItem.benchmarks && (
					<>
						<div className="h4">Referencia index</div>
						<HtmlBlock html={responseItem.benchmarks} />
					</>
				)}
				{responseItem.mutualFundManagerPerson && (
					<Table striped bordered>
						<tbody>
							<tr>
								<th scope="row">Alap menedzsere:</th>
								<td>{responseItem.mutualFundManagerPerson}</td>
							</tr>
						</tbody>
					</Table>
				)}
			</>
		);
	}

	protected getProductType(): ProductType {
		return "MUTUAL_FUND";
	}

	protected getComponentName(): string {
		return "MutualFundDataPage";
	}
}
